import { Hotel } from './../../core/models/hotel.interface';
import {
  MbDialog,
  MbBasicDialog,
  MbDialogHeader,
  MbDialogBody,
  MbPopoverTrigger,
  MbCard,
  MbCarousel,
  MbAdvice,
  MbIconText,
  MbIcon,
  MbTooltipTrigger,
  MbStarsRating,
  MbBadge,
  MbReviewsRating,
  MbReviewsEvaluation,
  MbReviewsDetail,
} from '@am/moebius';
import {
  AfterContentChecked,
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { HotelPromotion } from '@core/models/hotel-promotion.class';
import { AnalyticsService } from '@core/services/analytics.service';
import { select, Store } from '@ngrx/store';
import { selectPagination, selectRecommendedHotels } from '@results/store/results.selector';
import { UrlService } from '@services/url.service';
import {
  selectContextCore,
  selectContextCurrrencyCode,
  selectContextIsBot,
  selectContextLanguage,
} from '@store/context/context.selector';
import { IContextCore } from '@store/context/context.state';
import { IHotelsAppState } from '@store/index';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { getNested } from '../../../globals';
import { CountryId } from '../../models/enums/entities.enum';
import { HotelPriceComponent } from '../hotel-price/hotel-price.component';
import { recommendedHostelRequestGet } from '@results/store/results.actions';
import { mapStylesSettings } from '../hotels-map/hotels-map.configure';
import { RecommendedHotelsComponent } from '../recommended-hotels/recommended-hotels.component';
import { DeferLoadDirective } from '../../shared/directives/defer-load.directive';
import { GoogleMapsModule } from '@angular/google-maps';
import { AmExpertsShareComponent } from '@am/experts-share';
import { AmTripsAddProductComponent } from '@am/trips-add-product';
import { OutstandingPromoComponent } from '../outstanding-promo/outstanding-promo.component';
import { BackgroundImageDirective } from '../../shared/directives/background-image.directive';
import { NgClass, NgStyle, SlicePipe } from '@angular/common';

export const GOOGLE = new InjectionToken('google');
export const googleFactory = () => google;

@Component({
  selector: 'am-hotel',
  styleUrls: ['./hotel.component.scss'],
  templateUrl: './hotel.component.html',
  imports: [
    MbCard,
    NgClass,
    MbCarousel,
    BackgroundImageDirective,
    OutstandingPromoComponent,
    AmTripsAddProductComponent,
    MbAdvice,
    MbIconText,
    MbIcon,
    AmExpertsShareComponent,
    NgStyle,
    MbTooltipTrigger,
    MbBasicDialog,
    MbDialogHeader,
    MbDialogBody,
    GoogleMapsModule,
    MbStarsRating,
    MbBadge,
    MbReviewsRating,
    MbReviewsEvaluation,
    MbReviewsDetail,
    HotelPriceComponent,
    MbPopoverTrigger,
    DeferLoadDirective,
    RecommendedHotelsComponent,
    SlicePipe,
  ],
})
export class HotelComponent implements OnInit, OnDestroy, AfterContentChecked, AfterContentInit {
  @ViewChild('hotelPrice', { static: true }) public hotelPrice: HotelPriceComponent;
  @ViewChild(MbPopoverTrigger, { static: true }) public promotionPopover: MbPopoverTrigger;
  public promotion: HotelPromotion;

  @Input()
  public isLanding: any;
  @Input()
  public hotel: Hotel;
  @Input()
  public query: any;
  @Input()
  public isMobile: any;
  @Input()
  public DEFAULT_IMAGE: string;
  @Input()
  public urlResize: string;
  @Input()
  public locale: string;
  @Input()
  public nearBy: any;
  @Input()
  public hasRelated: boolean;
  @Input()
  public index: number;

  private unSubscribe$ = new Subject<void>();
  public language: string;
  public isBot: boolean;
  public showImage = false;
  public showAddressBadge = false;
  public isFeaturedId: boolean;
  public recommendedHotels: any;
  public recommendedHotelsReference: string;
  public tripFavoriteData: any;
  public currencyCodeToTripFavorite: string;
  public currencyCode: string;
  public context: IContextCore;
  public paymentMethodText: string;
  public imageUrls: string[];
  public brand: string;
  public ratingSummary: any = null;
  public googleMaps = google.maps;
  private mapStyles: google.maps.MapTypeStyle[] = mapStylesSettings;
  public center: google.maps.LatLngLiteral;
  public zoom = 14;
  public mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    gestureHandling: 'greedy',
    mapTypeId: this.googleMaps.MapTypeId.ROADMAP,
    styles: this.mapStyles,
  };
  public markerOptions = {
    hotelMarker: true,
    icon: {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(this.getMarkerSvg()),
      scaledSize: new google.maps.Size(48, 48),
      anchor: new google.maps.Point(24, 48),
    },
  };

  @ViewChild('mapDialog', { static: true })
  private mapDialog: TemplateRef<any>;
  private readonly hotelRatingLabels: Map<number, string>;

  constructor(
    private vcr: ViewContainerRef,
    private dialog: MbDialog,
    private store$: Store<IHotelsAppState>,
    private urlService: UrlService,
    private analyticsService: AnalyticsService,
    private detectedChanges: ChangeDetectorRef,
  ) {
    this.hotelRatingLabels = new Map([
      [5, 'REGULAR'],
      [6, 'ACEPTABLE'],
      [7, 'BUENO'],
      [8, 'MUY BUENO'],
      [9, 'EXCELENTE'],
      [10, 'FANTÁSTICO'],
      [-1, 'SIN PUNTUACIÓN'],
    ]);
  }

  ngAfterContentChecked(): void {
    this.detectedChanges.detectChanges();
  }

  ngAfterContentInit(): void {
    this.detectedChanges.detectChanges();
  }

  public ngOnInit() {
    this.promotion = this.setPromotion();
    this.setPaymentMethods();
    this.showAddressBadge = this.hotel.entities.some(
      (entity) => entity.entity_id === CountryId.MIAMI,
    );

    this.verifyIsFeatureId();
    this.store$
      .pipe(select(selectContextCore), takeUntil(this.unSubscribe$))
      .subscribe((contextCore) => {
        this.context = contextCore;
        this.brand = contextCore.brand;
      });
    this.store$
      .pipe(select(selectContextLanguage), takeUntil(this.unSubscribe$))
      .subscribe((language: string) => {
        this.language = language;
      });
    this.store$
      .pipe(select(selectContextIsBot), takeUntil(this.unSubscribe$))
      .subscribe((isBot: boolean) => {
        this.isBot = isBot;
      });
    this.store$
      .pipe(select(selectRecommendedHotels), takeUntil(this.unSubscribe$))
      .subscribe((recommendedHotels) => {
        this.recommendedHotels = recommendedHotels.hotels;
        this.recommendedHotelsReference = recommendedHotels.reference;
      });
    this.setTripFavoriteData();

    this.store$
      .pipe(select(selectContextCurrrencyCode), takeUntil(this.unSubscribe$))
      .subscribe((code: string) => {
        this.currencyCode = code;
      });

    this.imageUrls = this.resizeUrl();
    if (this.hotel.rating) {
      this.setHotelReviewRating(this.hotel.rating);
    }
  }

  private getMarkerSvg(): string {
    return `
        <svg width="100" height="130" viewBox="0 0 100 130" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="50" cy="120" rx="15" ry="5" fill="rgba(0,0,0,0.1)" />
        <path d="M50 120 C50 120 10 80 10 45 A40 40 0 1 1 90 45 C90 80 50 120 50 120 Z"
              fill="#175f9f" stroke="#ffffff" stroke-width="2"/>
        <path d="M35 50 L50 35 L65 50 V65 H55 V55 H45 V65 H35 Z"
              fill="white" />
        <path d="M32 52 L50 32 L68 52"
              fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    `;
  }

  private resizeUrl() {
    return this.hotel?.images?.length > 0
      ? this.hotel.images.map((urlImage) => {
          return this.urlResize + urlImage.url.replace('st.images', 'images');
        })
      : [];
  }

  public verifyIsFeatureId() {
    this.isFeaturedId =
      this.query &&
      this.query.queryParams &&
      this.query.queryParams.featuredHotelId === this.hotel.id;
  }

  public trackByItems(index) {
    return index;
  }

  public openDialog() {
    if (this.isBot) {
      return;
    }

    this.center = {
      lat: this.hotel.geo_position.latitude,
      lng: this.hotel.geo_position.longitude,
    };

    this.dialog.open(this.mapDialog, {
      closeOnBackdropClick: true,
      closeOnEscKey: true,
      closeBtn: true,
      size: 'lg',
      viewContainerRef: this.vcr,
    });
  }

  public setPromotion() {
    const commercial_arrangement = getNested(this.hotel, 'rate', 'commercial_arrangement');
    const promotion = getNested(this.hotel, 'rate', 'promotion');
    if (commercial_arrangement === 'EXCLUSIVE_DEAL') {
      return new HotelPromotion(commercial_arrangement);
    } else if (promotion) {
      return new HotelPromotion(promotion);
    } else {
      return this.promotion;
    }
  }

  public getUrl(hotel) {
    return this.urlService.getDetailRedirection(hotel).url;
  }

  public trackExpertsShareEvent() {
    this.analyticsService.trackEvent({
      action: 'Share',
      label: 'Click',
      section: 'Results',
    });
  }

  public detailRedirect() {
    this.trackEvents();
    const redirection = this.urlService.getDetailRedirection(this.hotel);
    window.open(redirection.url, redirection.self ? '_self' : '_blank');
    this.getRecommendedHotels();
  }

  public getRecommendedHotels() {
    const payload = {
      hotelId: this.hotel.id,
      ...this.query.entity,
      ...this.query.queryParams,
      isLanding: this.isLanding,
    };
    this.store$.dispatch(recommendedHostelRequestGet({ payload }));
  }

  public startPromotion() {
    if (this.promotion && this.promotion.type === 'rate') {
      this.promotionPopover.show();
      setTimeout(() => {
        this.finishPromotion();
      }, 5000);
    }
  }

  public setTripFavoriteData() {
    const room = this.query.queryParams.rooms;
    const dates = this.query.queryParams.date ? this.query.queryParams.date.split(',') : '';

    this.tripFavoriteData = Object.assign({}, this.hotel, {
      checkin: dates[0],
      checkout: dates[1],
      room,
    });

    this.currencyCodeToTripFavorite = this.hotel.rate.price.currency.code;
  }

  public finishPromotion() {
    this.promotionPopover.hide();
  }

  public ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.unsubscribe();
  }

  public trackEvents() {
    let index: any;
    this.store$.pipe(select(selectPagination), take(1)).subscribe((pagination: any) => {
      index = this.index ? this.index + pagination.offset : null;
    });
    this.analyticsService.trackEvent({
      action: 'Cluster selected',
      label: index,
      section: 'Results',
    });

    if (this.hotel.isEconomic) {
      this.analyticsService.trackEvent({
        action: 'Click Economic 4 o 5 estrellas',
        label: 'Show',
        section: 'Results',
      });
    }
    this.analyticsService.trackEvent({
      action: 'Cluster selected',
      label: this.hotel.isHomestays ? 'Alquiler selected' : 'Hotel selected',
      section: 'Results',
    });
  }

  public setPaymentMethods() {
    const prepaid: any = this.hotel.payment_methods.find((payment) => payment.code === 'PREPAID');
    const payAtDestination: any = this.hotel.payment_methods.find(
      (payment) => payment.code === 'PAY_AT_DESTINATION',
    );
    const paymentsParams = this.query?.queryParams?.PAYMENT_METHOD;
    if (paymentsParams && paymentsParams.split(',') !== 2) {
      this.paymentMethodText = paymentsParams;
    } else {
      this.paymentMethodText =
        prepaid && payAtDestination ? '_all' : prepaid ? 'PREPAID' : 'PAY_AT_DESTINATION';
    }
  }

  public hiddenPopover() {
    this.hotelPrice.hiddenPopover();
  }

  private setHotelReviewRating(rating: number): void {
    const ratingInteger: number = Math.floor(rating);
    const label: string = this.hotelRatingLabels.has(ratingInteger)
      ? this.hotelRatingLabels.get(ratingInteger)
      : this.hotelRatingLabels.get(-1);
    this.ratingSummary = {
      ...this.hotel.rating_summary,
      ratingLabel: label,
    };
  }
}
